import { ColumnsType } from 'antd/es/table';
import { message, Table } from 'antd';
import TitleBar from '../../../../components/TitleBar';
import { Footer } from '../../../../components/common/Footer';
import { useMutation, useQuery } from '@apollo/client';
import { ErrorMessages } from '../../../../constants';

import {
  CREATE_PRODUCT,
  DELETE_PRODUCT,
  EDIT_PRODUCT,
  FETCH_PRODUCT_BY_PRODUCT_GROUP,
} from '../../queries';
import { useParams } from 'react-router-dom';
import { tableSortingMethods } from '../../../../utils';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../components/generic/Table/TableV3';
import FinancialData from '../../../../components/common/FinancialData';
import { formFields } from './formFields';
import { baseColumns } from './exportColumns';
import { useUserContext } from '../../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../../components/error-handling';
import TitleUpdater from '../../../../components/common/TitleUpdater';

type GetAllProduct = {
  costOfSales: number;
  grossMargin: number;
  isDefault: boolean;
  uuid: string;
  name: string;
  percentage: number;
  revenue: number;
};

type DataReturned = {
  getAllProductsByProductGroup: GetAllProduct[];
};

function ProductFinancials() {
  const params = useParams();
  const corporateId = params.corporateId!;
  const corporateName = params.corporateName!;
  const divisionId = params.divisionId!;
  const divisionName = params.divisionName!;
  const productGroupId = params.productGroupId!;
  const productGroupName = params.productGroupName!;

  const { canViewFinancials } = useUserContext();

  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_PRODUCT_BY_PRODUCT_GROUP,
    {
      variables: {
        uuid: params.productGroupId,
      },
    },
  );

  const [createProduct] = useMutation(CREATE_PRODUCT, {
    refetchQueries: [`GetProductByProductGroup`],
  });

  const [updateProduct] = useMutation(EDIT_PRODUCT, {
    refetchQueries: [`GetProductByProductGroup`],
    onError: (error: Error) => {
      message.error(
        error.message ? error.message : ErrorMessages.OPERATION_FAILED,
      );
    },
  });

  const [deleteProduct] = useMutation(DELETE_PRODUCT, {
    refetchQueries: [`GetProductByProductGroup`],
  });

  const transformedData = data?.getAllProductsByProductGroup?.filter((item) => {
    return !item.isDefault;
  });

  const columns: ColumnsType<any> = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.revenue, b.revenue)
        : false,
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} />
        </span>
      ),
    },
    {
      title: 'Cost of Sales',
      dataIndex: 'costOfSales',
      key: 'costOfSales',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.costOfSales, b.costOfSales)
        : false,
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} />
        </span>
      ),
    },
    {
      title: 'Gross Margin',
      key: 'grossMargin',
      dataIndex: 'grossMargin',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.grossMargin, b.grossMargin)
        : false,
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} />
        </span>
      ),
    },
  ];

  const tableSummary = () => {
    const defaultRow = data?.getAllProductsByProductGroup?.filter((item) => {
      return item.isDefault;
    })!;

    if (!defaultRow) return;

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} className="custom-table--column text-sm">
          {defaultRow[0].name}
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={1}
          align={'right'}
          className="custom-table--column text-sm"
        >
          <FinancialData text={defaultRow[0].revenue} />
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={2}
          align={'right'}
          className="custom-table--column text-sm"
        >
          <FinancialData text={defaultRow[0].costOfSales} />
        </Table.Summary.Cell>
        <Table.Summary.Cell
          index={3}
          align={'right'}
          className="custom-table--column text-sm"
        >
          <FinancialData text={defaultRow[0].grossMargin} />
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  const modifyVariables = ({
    values,
    type,
    currentRecord,
  }: ModifyVariablesArgs) => {
    const revenue = parseFloat(String(values.revenue).replace(/,/g, ''));
    const costOfSales = parseFloat(
      String(values.costOfSales).replace(/,/g, ''),
    );

    if (type === 'create') {
      return {
        productGroupFinancial: { uuid: productGroupId },
        revenue: revenue,
        costOfSales: costOfSales,
        grossMargin: 0,
        name: values.name,
      };
    }

    return {
      revenue: revenue,
      costOfSales: costOfSales,
      grossMargin: currentRecord.grossMargin,
      name: values.name,
    };
  };

  return (
    <>
      <TitleUpdater title="Products Financials" />
      <TitleBar
        title="Data Inputs"
        subtitle={`Financials // ${productGroupName}`}
        brief="Within the selected product group, add the major products and associated financials below.
                       This step is optional and will make the analysis MEO can perform more accurate. "
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Product Financials"
          columns={columns}
          data={transformedData}
          loading={loading}
          editable
          summary={tableSummary}
          formFields={formFields}
          createGQL={createProduct}
          updateGQL={updateProduct}
          deleteGQL={deleteProduct}
          editableOptions={{
            create: canViewFinancials,
            update: canViewFinancials,
            delete: canViewFinancials,
          }}
          modifyVariables={modifyVariables}
          inlineEditing
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer
        backPath={`/${corporateName}/${corporateId}/${divisionName}/${divisionId}`}
      />
    </>
  );
}

export default ProductFinancials;
